import { get, off, onValue, ref, update } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { LaunchError } from "../utils/errors";
import { CREATIVE_TEMPLATE_PRODUCE_STATUS } from "../utils/constants";
import { canWriteCreativeTemplates } from "../features/creativeTemplates/utils/permissions";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";

const getProducePath = ({ advertiserID, produceID }) =>
  `creativeTemplatesProduce/${advertiserID}/${produceID}`;

export const startListenerCreativeTemplateProduce =
  ({
    produceID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !produceID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(db, getProducePath({ advertiserID, produceID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.exists() ? snapshot.val() : null);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeTemplateProduce",
          context: { produceID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeTemplateProduce =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startSaveCreativeTemplateProduce =
  ({
    produceID,
    templateID,
    language,
    rows,
    itemCount,
    artboardIDs,
    artboardCount,
    status,
    jobID,
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized" };
      }
      if (!advertiserID || !produceID || !templateID) {
        return { ok: false, reason: "missingParameters" };
      }

      const now = Date.now();
      const path = getProducePath({ advertiserID, produceID });
      const snapshot = await get(ref(db, path));
      const existing = snapshot.val() || {};
      const creationTime = existing.creationTime || now;
      const updates = {
        [`${path}/id`]: produceID,
        [`${path}/advertiserID`]: advertiserID,
        [`${path}/templateID`]: templateID,
        [`${path}/language`]: language,
        [`${path}/rows`]: rows,
        [`${path}/itemCount`]: itemCount,
        [`${path}/artboardIDs`]: artboardIDs || null,
        [`${path}/artboardCount`]: artboardCount || null,
        [`${path}/status`]:
          status || CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING,
        [`${path}/jobID`]: jobID || existing.jobID || null,
        [`${path}/error`]: null,
        [`${path}/summary`]: null,
        [`${path}/creatives`]: null,
        [`${path}/creationTime`]: creationTime,
        [`${path}/creationTimeNegative`]: -creationTime,
        [`${path}/updateTime`]: now,
        [`${path}/updateTimeNegative`]: -now,
      };

      await update(ref(db), updates);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSaveCreativeTemplateProduce",
          context: { produceID, templateID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveFailed" };
    }
  };

export const startUpdateCreativeTemplateProduceStatus =
  ({ produceID, status, error = null }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !produceID || !status) {
        return { ok: false, reason: "missingParameters" };
      }
      const now = Date.now();
      const path = getProducePath({ advertiserID, produceID });
      await update(ref(db), {
        [`${path}/status`]: status,
        [`${path}/error`]: error || null,
        [`${path}/updateTime`]: now,
        [`${path}/updateTimeNegative`]: -now,
      });
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUpdateCreativeTemplateProduceStatus",
          context: { produceID, status },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startSaveEditedProduceCreative =
  ({ produceID, creativeID, instance }) =>
  async (dispatch) => {
    try {
      const { role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized" };
      }
      if (!produceID || !creativeID || !instance) {
        return { ok: false, reason: "missingParameters" };
      }
      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "saveEditedProduceCreative"
        )}/saveEditedProduceCreative`,
        data: { produceID, creativeID, instance },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!result?.ok) {
        return { ok: false, message: result?.message };
      }
      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveEditedProduceCreative",
          context: { produceID, creativeID },
          tags: { action: "creativeTemplatesProduce" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveFailed", message: error?.message };
    }
  };
